import { createRoot } from 'react-dom/client';
import { Suspense } from 'react';

import '@dtsl/css-design-tokens/dist/index.css';
import '@dtsl/css-design-tokens/dist/themes/brevo/index.css';
import './assets/css/main.css';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';

import App from './App';
import AppStateProvider, { useAppState } from './state';
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import ErrorDialog from './components/ErrorDialog/ErrorDialog';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import theme from './theme';
import './types';
import { ChatProvider } from './components/ChatProvider';
import { ParticipantProvider } from './components/ParticipantProvider';
import { VideoProvider } from './components/VideoProvider';
import useConnectionOptions from './utils/useConnectionOptions/useConnectionOptions';
import UnsupportedBrowserWarning from './components/UnsupportedBrowserWarning/UnsupportedBrowserWarning';
import NotFound from './NotFound';
import './i18n/index';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import ErrorFallbackWrapper from './components/ErrorFallbackWrapper/ErrorFallbackWrapper';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const VideoApp = () => {
  const { error, setError, user } = useAppState();
  const connectionOptions = useConnectionOptions();

  return (
    <ErrorBoundary
      fallBackComponent={() => (
        <Suspense fallback={<div />}>
          <ErrorFallbackWrapper />
        </Suspense>
      )}
      user={user}
    >
      <UnsupportedBrowserWarning>
        <VideoProvider options={connectionOptions} onError={setError}>
          <ErrorDialog dismissError={() => setError(null)} error={error} />
          <ParticipantProvider>
            <ChatProvider>
              <App />
            </ChatProvider>
          </ParticipantProvider>
        </VideoProvider>
      </UnsupportedBrowserWarning>
    </ErrorBoundary>
  );
};

export const ReactApp = () => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppStateProvider>
          <Routes>
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <NotFound />
                </PrivateRoute>
              }
            />
            <Route
              path="/video/:URLExpertSlug/:URLRoom"
              element={
                <PrivateRoute>
                  <VideoApp />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppStateProvider>
      </Router>
    </ThemeProvider>
  </StyledEngineProvider>
);

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);
root.render(<ReactApp />);
